import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Export data to CSV format
 * @param {Array} data - Array of objects to export
 * @param {String} filename - Name of the file (without extension)
 */
export const exportToCSV = (data, filename = 'export') => {
  if (!data || data.length === 0) {
    console.error('No data to export');
    return;
  }

  // Get headers from first object
  const headers = Object.keys(data[0]);
  
  // Create CSV content
  const csvContent = [
    headers.join(','), // Header row
    ...data.map(row => 
      headers.map(header => {
        const value = row[header];
        // Handle values that contain commas, quotes, or newlines
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      }).join(',')
    )
  ].join('\n');

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
};

/**
 * Export data to Excel format
 * @param {Array} data - Array of objects to export
 * @param {String} filename - Name of the file (without extension)
 * @param {String} sheetName - Name of the Excel sheet
 */
export const exportToExcel = (data, filename = 'export', sheetName = 'Sheet1') => {
  if (!data || data.length === 0) {
    console.error('No data to export');
    return;
  }

  // Create workbook and worksheet
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(data);

  // Set column widths
  const maxWidth = 50;
  const colWidths = Object.keys(data[0]).map(key => {
    const maxLength = Math.max(
      key.length,
      ...data.map(row => String(row[key] || '').length)
    );
    return { wch: Math.min(maxLength + 2, maxWidth) };
  });
  ws['!cols'] = colWidths;

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  // Generate filename with date
  const dateStr = new Date().toISOString().split('T')[0];
  const excelFilename = `${filename}_${dateStr}.xlsx`;

  // Write file
  XLSX.writeFile(wb, excelFilename);
};

/**
 * Export data to PDF format
 * @param {Array} data - Array of objects to export
 * @param {String} filename - Name of the file (without extension)
 * @param {String} title - Title for the PDF document
 */
export const exportToPDF = (data, filename = 'export', title = 'Export Data') => {
  if (!data || data.length === 0) {
    console.error('No data to export');
    return;
  }

  // Create new PDF document
  const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation for better table fit
  
  // Add title
  doc.setFontSize(16);
  doc.text(title, 14, 15);
  
  // Add export date
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(`Exported on: ${new Date().toLocaleString()}`, 14, 22);

  // Prepare table data
  const headers = Object.keys(data[0]);
  const rows = data.map(row => 
    headers.map(header => {
      const value = row[header];
      if (value === null || value === undefined) return '';
      return String(value);
    })
  );

  // Add table
  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: 28,
    styles: {
      fontSize: 8,
      cellPadding: 2,
      overflow: 'linebreak',
    },
    headStyles: {
      fillColor: [66, 139, 202], // Blue header
      textColor: 255,
      fontStyle: 'bold',
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
    margin: { top: 28, left: 14, right: 14 },
    columnStyles: {},
  });

  // Generate filename with date
  const dateStr = new Date().toISOString().split('T')[0];
  const pdfFilename = `${filename}_${dateStr}.pdf`;

  // Save PDF
  doc.save(pdfFilename);
};

/**
 * Fetch and export users data
 * @param {String} format - Export format: 'csv', 'excel', or 'pdf'
 * @param {Object} filters - Filter parameters (search, daterange, status, sort_by)
 * @param {Function} onStart - Callback when export starts
 * @param {Function} onComplete - Callback when export completes
 * @param {Function} onError - Callback when export fails
 */
export const exportUsers = async (format, filters = {}, onStart, onComplete, onError) => {
  try {
    if (onStart) onStart();

    // Build query string
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.daterange) {
      // Handle daterange - could be array or string
      if (Array.isArray(filters.daterange)) {
        params.append('daterange', filters.daterange.join(','));
      } else {
        params.append('daterange', filters.daterange);
      }
    }
    if (filters.status !== undefined && filters.status !== '') {
      params.append('status', filters.status);
    }
    if (filters.sort_by) params.append('sort_by', filters.sort_by);

    // Import ApiService dynamically to avoid circular dependencies
    const ApiService = (await import('../services/ApiService')).default;
    
    // Fetch all users data
    const response = await ApiService.request({
      method: 'GET',
      url: `admin/users/export?${params.toString()}`,
    });

    if (response.data.status && response.data.data) {
      const data = response.data.data;
      
      // Export based on format
      switch (format.toLowerCase()) {
        case 'csv':
          exportToCSV(data, 'users');
          break;
        case 'excel':
          exportToExcel(data, 'users', 'Users');
          break;
        case 'pdf':
          exportToPDF(data, 'users', 'Users Export');
          break;
        default:
          throw new Error(`Unsupported export format: ${format}`);
      }

      if (onComplete) onComplete();
    } else {
      throw new Error(response.data.message || 'Failed to fetch users data');
    }
  } catch (error) {
    console.error('Export error:', error);
    if (onError) onError(error);
    throw error;
  }
};

/**
 * Fetch and export bookings data
 * @param {String} format - Export format: 'csv', 'excel', or 'pdf'
 * @param {Object} filters - Filter parameters (search, daterange, status, payment_status, shop_id, sort_by, sort_order)
 * @param {Function} onStart - Callback when export starts
 * @param {Function} onComplete - Callback when export completes
 * @param {Function} onError - Callback when export fails
 */
const toYMD = (value) => {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Flatten a raw booking record into a clean, human-readable row for export.
 * Nested objects (user, partner, services) are reduced to plain strings so
 * they don't render as "[object Object]" in CSV/Excel/PDF.
 */
const mapBookingForExport = (b) => {
  const services = b.services || [];
  const serviceNames = services.map((s) => s?.service_id?.title).filter(Boolean).join(', ');
  const roomNames = services.map((s) => s?.room_id?.title).filter(Boolean).join(', ');
  const durations = services.map((s) => s?.duration).filter(Boolean).join(', ');

  return {
    'Booking ID': b.booking_code || (b._id ? `#${String(b._id).slice(-6)}` : ''),
    Customer: b.user_id?.name || (b.guest_name ? `${b.guest_name} (Guest)` : ''),
    Email: b.user_id?.email || '',
    Phone: b.user_id?.phone || b.guest_phone || '',
    Service: serviceNames || '—',
    Room: roomNames || '—',
    Duration: durations || '—',
    'Booking Date': toYMD(b.booking_date),
    'Booking Time': b.booking_time || '',
    Status: b.status || '',
    'Paid By': b.payment_by || '',
    'Payment Status': b.payment_status || '',
    'Booked On': toYMD(b.createdAt),
  };
};

export const exportBookings = async (format, filters = {}, onStart, onComplete, onError) => {
  try {
    if (onStart) onStart();

    // Build query string – mirror the admin bookings list filters so the export
    // matches exactly what the user is currently viewing.
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);

    // Handle date range - bookings API uses date_from and date_to
    if (filters.daterange) {
      if (Array.isArray(filters.daterange) && filters.daterange.length === 2) {
        const [start, end] = filters.daterange;
        if (start) params.append('date_from', toYMD(start));
        if (end) params.append('date_to', toYMD(end));
      } else if (typeof filters.daterange === 'string') {
        const parts = filters.daterange.split(',');
        if (parts.length === 2) {
          params.append('date_from', parts[0]);
          params.append('date_to', parts[1]);
        }
      }
    }

    if (filters.status !== undefined && filters.status !== '') {
      params.append('status', filters.status);
    }
    if (filters.payment_status !== undefined && filters.payment_status !== '') {
      params.append('payment_status', filters.payment_status);
    }
    if (filters.booking_source !== undefined && filters.booking_source !== '') {
      params.append('booking_source', filters.booking_source);
    }
    // Pull all matching records (not just the current page).
    params.append('limit', '10000');
    params.append('page', '1');

    // Import ApiService
    const ApiService = (await import('../services/ApiService')).default;

    // Fetch all bookings data from the same endpoint the list uses.
    const response = await ApiService.request({
      method: 'GET',
      url: `admin/bookings?${params.toString()}`,
    });

    if (response.data.status) {
      const raw = response.data.data || [];

      if (!raw.length) {
        throw new Error('No bookings found for the selected filters');
      }

      const data = raw.map(mapBookingForExport);

      // Export based on format
      switch (format.toLowerCase()) {
        case 'csv':
          exportToCSV(data, 'bookings');
          break;
        case 'excel':
          exportToExcel(data, 'bookings', 'Bookings');
          break;
        case 'pdf':
          exportToPDF(data, 'bookings', 'Bookings Export');
          break;
        default:
          throw new Error(`Unsupported export format: ${format}`);
      }

      if (onComplete) onComplete();
    } else {
      throw new Error(response.data.message || 'Failed to fetch bookings data');
    }
  } catch (error) {
    console.error('Export error:', error);
    if (onError) onError(error);
    throw error;
  }
};

